import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useWindowSize } from "lib/hooks";
import log from "loglevel";
import { Button, Modal, Container, Row, Col } from "react-bootstrap";
// @ts-ignore
import BootstrapTable from "react-bootstrap-table-next";
// @ts-ignore
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
// @ts-ignore
import paginationFactory from "react-bootstrap-table2-paginator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  addConfigKeyAction,
  modifyConfigKeyAction,
  deleteConfigKeyAction,
} from "store/actions/actionCreators";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import {
  Config,
  ConfigDefault,
  CreateConfigKeyRequestType,
  ModifyConfigKeyRequestType,
  DeleteConfigKeyRequestType,
} from "types";
import { xIfFalseFormatter, options } from "util/reactTableFuncs";
import { checkIconIfTrue } from "util/reactSnippets";
import { int2Bool } from "util/utilityFuncs";
import { Table, Form } from "@avlino-inc/alenza-react";
import styles from "components/dashboard/Dashboard.module.scss";

type CreateConfigFormType = {
  key_name: string | undefined;
  key_value: string | undefined;
  is_removable: boolean | undefined;
  is_modifiable: boolean | undefined;
  is_sensitive: boolean | undefined;
};

const defaultCreateConfig = {
  key_name: undefined,
  key_value: undefined,
  is_removable: true,
  is_modifiable: true,
  is_sensitive: false,
};

type ModifyConfigKeyFormType = {
  id: number | undefined;
  key_value?: string | undefined;
  is_removable: boolean | undefined;
  is_modifiable: boolean | undefined;
  is_sensitive: boolean | undefined;
};

const defaultModifyConfig = {
  id: undefined,
  key_value: undefined,
  is_removable: undefined,
  is_modifiable: undefined,
  is_sensitive: undefined,
};

const ConfigKeyValueTable = ({
  config,
  configDefault,
  showCreateModal,
  setShowCreateModal,
  showSearch,
}: {
  config: Config[];
  configDefault: ConfigDefault;
  showCreateModal: boolean;
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
  showSearch: boolean;
}) => {
  const dispatch = useDispatch();
  const windowSize = useWindowSize();

  const [selectedConfigKey, setSelectedConfigKey] = useState<Config>();
  const [atLeastOneFieldChanged, setAtLeastOneFieldChanged] = useState<boolean>(
    false
  );
  const [createConfigKey, setCreateConfigKey] = useState<CreateConfigFormType>(
    defaultCreateConfig
  );
  const [modifyConfigKey, setModifyConfigKey] = useState<
    ModifyConfigKeyFormType
  >(defaultModifyConfig);
  // eslint-disable-next-line
  const [isCreatingConfigKey, setIsCreatingConfigKey] = useState(false);
  // eslint-disable-next-line
  const [isModifyingConfigKey, setIsModifyingConfigKey] = useState(false);
  // eslint-disable-next-line
  const [isDeletingConfigKey, setIsDeletingConfigKey] = useState(false);

  // NOTE: Moved to outside component!
  // const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const validCreateConfigKeyFields = (
    keyForm: CreateConfigFormType | undefined
  ) => {
    return keyForm && keyForm.key_name && keyForm?.key_value;
  };

  /**
   * Create a new Configuration Key by dispatching a Redux Action
   * @param event
   */
  const onHandleCreateKeyValueClick = (event: any) => {
    event.preventDefault();
    if (
      createConfigKey &&
      createConfigKey.key_name &&
      createConfigKey.key_value &&
      createConfigKey.is_removable !== undefined &&
      createConfigKey.is_modifiable !== undefined &&
      createConfigKey.is_sensitive !== undefined
    ) {
      log.info("All fields are valid! Creating User...");
      const request: CreateConfigKeyRequestType = {
        body: {
          key: createConfigKey.key_name,
          value: createConfigKey.key_value,
          is_removable: createConfigKey.is_removable,
          is_modifiable: createConfigKey.is_modifiable,
          is_sensitive: createConfigKey.is_sensitive,
        },
      };
      log.info("-- Dispatching addConfigKeyAction()...");
      log.info("Request: " + JSON.stringify(request));
      dispatch(addConfigKeyAction(request));
      setIsCreatingConfigKey(false);
      setShowCreateModal(false);
      setCreateConfigKey(defaultCreateConfig); // clear
    } else {
      log.info("One or more fields NOT VALID! Will not create Config Key.");
      log.info(JSON.stringify(createConfigKey));
    }
  };

  /**
   * Modify an existing Configuration Key by dispatching a Redux Action
   * @param event
   */
  const onHandleModifyKeyValueClick = (event: any) => {
    event.preventDefault();
    // Updating the value is optional
    if (
      selectedConfigKey &&
      modifyConfigKey &&
      modifyConfigKey.is_removable !== undefined &&
      modifyConfigKey.is_modifiable !== undefined &&
      modifyConfigKey.is_sensitive !== undefined
    ) {
      log.info("All fields are valid! Creating Config Key ...");
      const request: ModifyConfigKeyRequestType = {
        id: selectedConfigKey.id,
        body: {
          is_removable: modifyConfigKey.is_removable,
          is_modifiable: modifyConfigKey.is_modifiable,
          is_sensitive: modifyConfigKey.is_sensitive,
        },
      };
      if (modifyConfigKey.key_value !== undefined) {
        request.body.value = modifyConfigKey.key_value;
      }
      log.info("-- Dispatching modifyConfigKeyAction()...");
      dispatch(modifyConfigKeyAction(request));
      setIsModifyingConfigKey(false);
      setShowSettingsModal(false);
      setSelectedConfigKey(undefined); // clear
      setModifyConfigKey(defaultModifyConfig); // clear
    } else {
      log.info("One or more fields NOT VALID! Will not modify Config Key.");
    }
  };

  /**
   * Delete an existing Configuration Key by dispatching a Redux Action
   * @param event
   */
  const onHandleDeleteConfigKeyValueClick = (event: any) => {
    event.preventDefault();
    if (selectedConfigKey && selectedConfigKey.id) {
      const request: DeleteConfigKeyRequestType = {
        id: selectedConfigKey.id,
      };
      log.info("-- Dispatching deleteUserAction()...");
      dispatch(deleteConfigKeyAction(request));
      setIsDeletingConfigKey(false);
      setShowSettingsModal(false);
      setSelectedConfigKey(undefined); // clear
    }
  };

  /**
   * Create a JSX element displaying Config values as a table
   * @param configKeyValues an array of Config objects
   */
  const configTable = (configKeyValues: Config[]) => {
    const { SearchBar } = Search;
    const adjustedOptions = { ...options }; // deep copy
    adjustedOptions.hideSizePerPage = false;
    adjustedOptions.sizePerPageList = [
      {
        text: "Show 10",
        value: 10,
      },
      {
        text: "Show 25",
        value: 25,
      },
      {
        text: "Show 50",
        value: 50,
      },
    ];
    let columns = [
      {
        dataField: "key",
        text: "Key",
        sort: true,
        headerAlign: "left",
        align: "left",
        headerStyle: () => {
          return { width: "100%" };
        },
      },
      {
        dataField: "value",
        text: "Value",
        hidden: true,
      },
    ];
    if (windowSize.width > 575) {
      columns.push({
        dataField: "value",
        text: "Value",
        sort: true,
        headerAlign: "left",
        align: "left",
        headerStyle: () => {
          return { width: "100%" };
        },
      });
    }
    if (windowSize.width > 700) {
      columns.unshift({
        dataField: "key_id",
        text: "ID",
        sort: true,
        headerAlign: "left",
        align: "left",
        headerStyle: () => {
          return { width: "80px" };
        },
      });
    }
    if (
      windowSize.width >= 1400 ||
      (windowSize.width <= 1200 && windowSize.width > 900)
    ) {
      columns.push({
        dataField: "is_removable",
        text: "R",
        // @ts-ignore
        formatter: xIfFalseFormatter,
        headerAlign: "center",
        align: "center",
        headerStyle: () => {
          return { width: "60px" };
        },
      });
      columns.push({
        dataField: "is_modifiable",
        text: "M",
        // @ts-ignore
        formatter: xIfFalseFormatter,
        headerAlign: "center",
        align: "center",
        headerStyle: () => {
          return { width: "60px" };
        },
      });
      columns.push({
        dataField: "is_sensitive",
        text: "S",
        // @ts-ignore
        formatter: xIfFalseFormatter,
        headerAlign: "center",
        align: "center",
        headerStyle: () => {
          return { width: "60px" };
        },
      });
    }
    const rowEvents = {
      onClick: (e: any, row: Config, rowIndex: number) => {
        log.info("Row Index: " + rowIndex);
        log.info("Row: " + JSON.stringify(row));
        const isModifiableParsed = int2Bool(row.is_modifiable);
        const isRemovableParsed = int2Bool(row.is_removable);
        const isSensitiveParsed = int2Bool(row.is_sensitive);
        setSelectedConfigKey(row);
        setModifyConfigKey({
          id: row.id,
          key_value: !isSensitiveParsed ? row.value : undefined,
          is_modifiable: isModifiableParsed,
          is_removable: isRemovableParsed,
          is_sensitive: isSensitiveParsed,
        });
        setShowSettingsModal(true);
      },
    };
    let dataSorted: Config[] =
      configKeyValues &&
      configKeyValues.sort((a: Config, b: Config) =>
        a.key.localeCompare(b.key)
      );
    return (
      <>
        {dataSorted ? (
          <ToolkitProvider
            keyField="id"
            data={dataSorted}
            columns={columns}
            search
          >
            {(props: any) => (
              <>
                {showSearch ? (
                  <div className={styles["table__search-container"]}>
                    <SearchBar
                      className={styles["table__search-container__search-bar"]}
                      {...props.searchProps}
                    />
                  </div>
                ) : null}
                <div className={Table.classNames.table}>
                  <BootstrapTable
                    {...props.baseProps}
                    bordered={false}
                    bootstrap4
                    rowEvents={rowEvents}
                    pagination={paginationFactory(adjustedOptions)}
                  />
                </div>
              </>
            )}
          </ToolkitProvider>
        ) : null}
      </>
    );
  };

  /**
   * Create a JSX element as a series of checkboxes
   * @param isCreate specify is this JSX element is for Config creation
   */
  const configOptions = (isCreate: boolean) => {
    return (
      <>
        <Row style={{ paddingTop: "20px" }}>
          <Col>
            <label>
              <span>Modifiable: </span>
              <input
                type="checkbox"
                defaultChecked={
                  isCreate
                    ? createConfigKey?.is_modifiable
                    : modifyConfigKey?.is_modifiable
                }
                onClick={(e) => {
                  if (isCreate) {
                    setCreateConfigKey((state): typeof state => ({
                      ...state,
                      is_modifiable: createConfigKey?.is_modifiable
                        ? false
                        : true,
                    }));
                  } else {
                    setModifyConfigKey((state): typeof state => ({
                      ...state,
                      is_modifiable: createConfigKey?.is_modifiable
                        ? false
                        : true,
                    }));
                    setAtLeastOneFieldChanged(true);
                  }
                }}
              />
            </label>
          </Col>

          <Col>
            <label>
              <span>Removable: </span>
              <input
                type="checkbox"
                defaultChecked={
                  isCreate
                    ? createConfigKey?.is_removable
                    : modifyConfigKey?.is_removable
                }
                onClick={(e) => {
                  if (isCreate) {
                    setCreateConfigKey((state): typeof state => ({
                      ...state,
                      is_removable: createConfigKey?.is_removable
                        ? false
                        : true,
                    }));
                  } else {
                    setModifyConfigKey((state): typeof state => ({
                      ...state,
                      is_removable: modifyConfigKey?.is_removable
                        ? false
                        : true,
                    }));
                    setAtLeastOneFieldChanged(true);
                  }
                }}
              />
            </label>
          </Col>

          <Col>
            <label>
              <span>Sensitive: </span>
              <input
                type="checkbox"
                defaultChecked={
                  isCreate
                    ? createConfigKey?.is_sensitive
                    : modifyConfigKey?.is_sensitive
                }
                onClick={(e: any) => {
                  if (isCreate) {
                    setCreateConfigKey((state): typeof state => ({
                      ...state,
                      is_sensitive: createConfigKey?.is_sensitive
                        ? false
                        : true,
                    }));
                  } else {
                    setModifyConfigKey((state): typeof state => ({
                      ...state,
                      is_sensitive: createConfigKey?.is_sensitive
                        ? false
                        : true,
                    }));
                    setAtLeastOneFieldChanged(true);
                  }
                }}
              />
            </label>
          </Col>
        </Row>
      </>
    );
  };

  /**
   * Create a JSX element for Configuration table
   */
  const configCard = () => {
    return <>{configTable(config)}</>;
  };

  /**
   * Create a JSX element for Configuration Create Modal
   */
  const createConfigModal = () => {
    return (
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showCreateModal}
          onHide={() => {
            setShowCreateModal(false);
            setCreateConfigKey(defaultCreateConfig);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Create Key <FontAwesomeIcon icon={["fal", "key"]} />
            </Modal.Title>
          </Modal.Header>
          <form name="create-keyvalue-form">
            <Modal.Body>
              <div className="w-100" style={{ textAlign: "center" }}>
                <Form.Group controlId="create_key">
                  <Form.Input
                    type="text"
                    placeholder="Key Name"
                    status={
                      createConfigKey.key_name &&
                      createConfigKey.key_name !== undefined
                        ? "valid"
                        : "invalid"
                    }
                    onChange={(e: any) => {
                      if (e.target instanceof HTMLInputElement) {
                        const value = e.target.value;
                        setCreateConfigKey((state): typeof state => ({
                          ...state,
                          key_name: value,
                        }));
                      }
                    }}
                  />
                </Form.Group>

                <Form.Group controlId="create_key_value">
                  <Form.Input
                    rows={3}
                    columns={50}
                    type="text"
                    maxLength={150}
                    placeholder="Enter your key value here"
                    value={createConfigKey?.key_value}
                    style={{ resize: "none" }}
                    onChange={(e: any) => {
                      if (e.target instanceof HTMLTextAreaElement) {
                        const value = e.target.value;
                        setCreateConfigKey((state): typeof state => ({
                          ...state,
                          key_value: value,
                        }));
                      }
                    }}
                  />
                </Form.Group>
              </div>
              {configOptions(true)}
            </Modal.Body>
            <Modal.Footer>
              <Button
                type="submit"
                disabled={!validCreateConfigKeyFields(createConfigKey)}
                variant="success"
                onClick={(e: any) => {
                  onHandleCreateKeyValueClick(e);
                }}
              >
                Create
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      </>
    );
  };

  /**
   * Create a JSX element for Configuration Modify Modal
   */
  const editConfigModal = () => {
    return (
      <>
        <Modal
          id="key-value-settings-modal"
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showSettingsModal}
          onHide={() => {
            setShowSettingsModal(false);
            setModifyConfigKey(defaultModifyConfig);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Update or Delete Key
              <FontAwesomeIcon
                style={{ marginLeft: "5px" }}
                icon={["fal", "key"]}
              />
            </Modal.Title>
          </Modal.Header>
          <>
            <Modal.Body>
              <Container>
                <Row>
                  <Col md={12}>
                  <h5>Key</h5>
                    <p className="modal-header-key-display">
                      <textarea
                        className="w-100"
                        disabled={true}
                        style={{ fontStyle: "italic", resize: "none" }}
                      >
                        {selectedConfigKey?.key}
                      </textarea>
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p>
                      <span className="line-title">Removable: </span>
                      <span>
                        {checkIconIfTrue(selectedConfigKey?.is_removable)}
                      </span>
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p>
                      <span className="line-title">Modifiable: </span>
                      <span>
                        {checkIconIfTrue(selectedConfigKey?.is_modifiable)}
                      </span>
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <p>
                      <span className="line-title">Sensitive: </span>
                      <span>
                        {checkIconIfTrue(selectedConfigKey?.is_sensitive)}
                      </span>
                    </p>
                  </Col>
                </Row>
              </Container>
            </Modal.Body>
            <form name="modify-form">
              <Modal.Body>
                <h5>Update</h5>
                {selectedConfigKey?.is_modifiable ? (
                  <>
                    <p style={{ fontStyle: "italic" }}>
                      Leave text area blank to keep existing key value
                    </p>
                    <textarea
                      style={{ resize: "none", marginBottom: "10px" }}
                      rows={5}
                      cols={50}
                      name="edit_key_value"
                      placeholder={
                        selectedConfigKey.is_sensitive
                          ? "Selected value is sensitive and being hidden"
                          : undefined
                      }
                      onChange={(e) => {
                        setModifyConfigKey({
                          id: selectedConfigKey.id,
                          key_value: e.target.value,
                          is_modifiable: modifyConfigKey?.is_modifiable,
                          is_removable: modifyConfigKey?.is_removable,
                          is_sensitive: modifyConfigKey?.is_sensitive,
                        });
                        setAtLeastOneFieldChanged(true);
                      }}
                    >
                      {modifyConfigKey?.key_value}
                    </textarea>
                    {configOptions(false)}
                    <Button
                      className="float-right"
                      variant="success"
                      disabled={!atLeastOneFieldChanged}
                      onClick={(e: any) => {
                        onHandleModifyKeyValueClick(e);
                      }}
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <p style={{ fontStyle: "italic" }}>
                    This key has been marked as not modifiable and is unable to
                    be modified.
                  </p>
                )}
              </Modal.Body>
            </form>
            <hr />
            <form name="delete-form" style={{ marginBottom: "15px" }}>
              <Modal.Body>
                <h5>Delete</h5>
                {selectedConfigKey?.is_removable ? (
                  <>
                    <p>
                      Are you sure you want to delete this Configuration Key?
                    </p>
                    <div>
                      <Button
                        className="float-right"
                        variant="danger"
                        onClick={(e: any) => {
                          onHandleDeleteConfigKeyValueClick(e);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </>
                ) : (
                  <p style={{ fontStyle: "italic" }}>
                    This key has been marked as not removable and is unable to
                    be deleted.
                  </p>
                )}
              </Modal.Body>
            </form>
          </>
        </Modal>
      </>
    );
  };

  return (
    <>
      {configCard()}
      {createConfigModal()}
      {editConfigModal()}
    </>
  );
};

export default ConfigKeyValueTable;
